import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Ban, MessageSquareWarning } from 'lucide-react';
import { responseService } from '../../../services/auditService';
import type { ResponseRole } from './responseShellTypes';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import FormControl from '../../common/FormControl';

/**
 * RejectNoteCell — shared per-line reject-note control (Phase 108 G2).
 *
 * Mirrors the EvidenceCell rollout pattern from Phase 107: ONE icon-only
 * affordance that drops into every response body's per-row Actions cluster.
 *
 * Two modes, chosen by `role` + `readOnly`:
 *   - CHECKER (role === 'approver', not readOnly): clicking opens a small modal
 *     with a note textarea + a Reject button (persists reject_note via
 *     responseService.update(procedureType, responseId, { reject_note })) and a
 *     Clear button (sends an empty string => server stores NULL => un-rejected).
 *   - MAKER / read-only (any other role, OR readOnly/Approved): the control is
 *     view-only. When a reject_note is present it shows a red "Rejected" chip and
 *     clicking opens the note read-only; when absent it renders nothing.
 *
 * A non-empty reject_note marks the line rejected. Each body sums these into a
 * `rejectedCount` that ResponseShell uses to disable Approve + show a maker badge.
 *
 * Rows without a saved response_id cannot be rejected yet (the checker reject
 * needs an id to PUT against); the control disables itself in that case.
 *
 * data-testid: BTN_reject_note_<responseId> (button), INPUT_reject_note_<responseId> (textarea).
 */

export interface RejectNoteCellProps {
  /** The saved response row id this note attaches to. Undefined => not yet savable. */
  responseId?: number;
  /** Current persisted reject note (non-empty => line rejected). */
  rejectNote?: string | null;
  /**
   * procedure_type key understood by responseService (Checklist, Questionnaire,
   * Form, Rating, Confirmation, PhysicalCheck, Walkthrough, RiskAssessment,
   * ControlTest). Drives which PUT route the note is persisted to.
   */
  procedureType: string;
  /** The acting user's role on this instance. Checker = 'approver'. */
  role: ResponseRole;
  /** G3 (108-01) approved-lock — when true, the checker input is disabled too. */
  readOnly?: boolean;
  /** Called after a successful persist with the new note value ('' on clear). */
  onSaved: (note: string) => void;
}

const RejectNoteCell: React.FC<RejectNoteCellProps> = ({
  responseId,
  rejectNote,
  procedureType,
  role,
  readOnly = false,
  onSaved,
}) => {
  const isChecker = role === 'approver' && !readOnly;
  const hasNote = !!(rejectNote && rejectNote.trim());

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(rejectNote ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  // Keep the local draft in sync when the persisted value changes underneath us
  // (e.g. after a parent reload) while the modal is closed.
  useEffect(() => {
    if (!open) setDraft(rejectNote ?? '');
  }, [rejectNote, open]);

  const persist = useCallback(
    async (note: string) => {
      if (responseId == null) return;
      setBusy(true);
      setError(null);
      try {
        await responseService.update(procedureType, responseId, { reject_note: note });
        if (!mounted.current) return;
        onSaved(note);
        setOpen(false);
      } catch (e: any) {
        if (mounted.current) {
          setError(e?.response?.data?.detail ?? e?.message ?? 'Failed to save reject note.');
        }
      } finally {
        if (mounted.current) setBusy(false);
      }
    },
    [responseId, procedureType, onSaved],
  );

  // Maker / read-only with no note → render nothing (keeps the cluster clean).
  if (!isChecker && !hasNote) return null;

  const disabled = responseId == null;
  const tooltip = disabled
    ? 'Save this line before rejecting it.'
    : isChecker
      ? hasNote
        ? 'Edit / clear reject note'
        : 'Reject this line with a note'
      : hasNote
        ? 'View reject note'
        : 'Reject note';

  return (
    <span className="response-reject-note-cell">
      <button
        type="button"
        className={`response-row-action${hasNote ? ' response-row-action--rejected' : ''}`}
        onClick={() => { if (!disabled) { setDraft(rejectNote ?? ''); setOpen(true); } }}
        disabled={disabled}
        title={tooltip}
        aria-label={tooltip}
        data-testid={`BTN_reject_note_${responseId ?? 'pending'}`}
      >
        {hasNote ? <Ban size={16} /> : <MessageSquareWarning size={16} />}
        {hasNote && <span className="response-action-dot response-action-dot--danger" aria-hidden="true" />}
      </button>

      {open && (
        <Modal isOpen onClose={() => setOpen(false)} size="md">
          <Modal.Header onClose={() => setOpen(false)}>
            {isChecker ? 'Reject this line' : 'Reject note'}
          </Modal.Header>
          <Modal.Body>
            {isChecker ? (
              <FormControl.Textarea
                label="Reason for rejection"
                data-testid={`INPUT_reject_note_${responseId ?? 'pending'}`}
                value={draft}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDraft(e.target.value)}
                rows={4}
                autoFocus
                placeholder="Explain why this line is being sent back to the maker…"
              />
            ) : (
              <p className="response-reject-note-readonly" data-testid={`TXT_reject_note_${responseId ?? 'pending'}`}>
                {hasNote ? rejectNote : 'No reject note on this line.'}
              </p>
            )}
            {error && <div className="response-inline-warning">{error}</div>}
          </Modal.Body>
          <Modal.Footer>
            {isChecker ? (
              <>
                {hasNote && (
                  <Button
                    variant="secondary-outline"
                    data-testid={`BTN_reject_note_clear_${responseId ?? 'pending'}`}
                    onClick={() => persist('')}
                    disabled={busy}
                  >
                    {busy ? 'Clearing…' : 'Clear (un-reject)'}
                  </Button>
                )}
                <Button
                  variant="delete-outline"
                  data-testid={`BTN_reject_note_submit_${responseId ?? 'pending'}`}
                  onClick={() => persist(draft)}
                  disabled={busy || !draft.trim()}
                >
                  {busy ? 'Saving…' : 'Reject line'}
                </Button>
              </>
            ) : (
              <Button variant="fill" onClick={() => setOpen(false)}>Close</Button>
            )}
          </Modal.Footer>
        </Modal>
      )}
    </span>
  );
};

export default RejectNoteCell;
export { RejectNoteCell };
